if (!customElements.get('bt-collections-filtering')) {
  customElements.define('bt-collections-filtering', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.generation = this.querySelector('select[data-role="generation"]');
      this.model = this.querySelector('select[data-role="baureihe"]');
      this.category = this.querySelector('select[data-role="category"]');
      if (!this.form || !this.button || !this.generation || !this.model || !this.category) return;
      this.labels = JSON.parse(this.querySelector('[data-bt-filter-labels]')?.textContent || '{}');
      this.brand = 'BMW';
      this.brandButtons = [...this.querySelectorAll('[data-bt-brand]')];
      this.status = document.createElement('p');
      this.status.className = 'bt-filter-status';
      this.status.setAttribute('role', 'status');
      this.form.append(this.status);
      this.querySelectorAll('button.tooltip').forEach(button => { button.type = 'button'; });
      this.form.addEventListener('submit', event => this.submit(event));
      this.generation.addEventListener('change', () => this.changeGeneration());
      this.model.addEventListener('change', () => this.changeModel());
      this.category.addEventListener('change', () => this.updateDestination());
      this.brandButtons.forEach(button => button.addEventListener('click', () => {
        if (this.brand !== button.dataset.btBrand) this.changeBrand(button.dataset.btBrand);
      }));
      this.load();
    }
    get storageKey() { return 'bimituning:selected-vehicle'; }
    brandFor(code) { return ['R56', 'F56'].includes(code) ? 'MINI' : 'BMW'; }
    categoryKey(item) { return item.url || item.handle + (item.vehicleTag ? '/' + item.vehicleTag : ''); }
    savedSelection() {
      try { return JSON.parse(window.localStorage.getItem(this.storageKey) || '{}') || {}; }
      catch { return {}; }
    }
    selectionFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return (params.get('bt_vehicle') || params.get('vehicle') || '').toUpperCase();
    }
    saveSelection() {
      if (!this.model.value) return;
      const selection = {brand: this.brand, generation: this.generation.value, model: this.model.value, category: this.category.value};
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(selection));
        window.sessionStorage.setItem('bt-vehicle', selection.model);
      } catch {}
      window.dispatchEvent(new CustomEvent('bimituning:vehicle-changed', {detail: selection}));
    }
    clearSelection() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify({brand: this.brand}));
        window.sessionStorage.removeItem('bt-vehicle');
      } catch {}
      window.dispatchEvent(new CustomEvent('bimituning:vehicle-changed', {detail: null}));
    }
    option(value, label) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      return option;
    }
    setOptions(select, placeholder, entries, enabled) {
      select.replaceChildren(this.option('', placeholder));
      entries.forEach(([value, label]) => select.append(this.option(value, label)));
      select.value = '';
      select.disabled = !enabled;
      select.closest('.form-group')?.classList.toggle('disabled', !enabled);
    }
    async load() {
      this.button.disabled = true;
      this.brandButtons.forEach(button => { button.disabled = true; });
      this.setOptions(this.generation, this.labels.loading, [], false);
      this.setOptions(this.model, this.labels.choose_generation_first, [], false);
      this.setOptions(this.category, this.labels.choose_model_first, [], false);
      try {
        const response = await fetch(this.dataset.btSource, {credentials: 'same-origin'});
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();
        this.models = data.models || {};
        this.brandButtons.forEach(button => { button.disabled = false; });
        this.restoreSelection();
      } catch {
        this.status.textContent = this.labels.load_error;
        const link = document.createElement('a');
        link.href = this.dataset.catalogUrl;
        link.textContent = ' ' + this.labels.catalog;
        this.status.append(link);
      }
    }
    changeBrand(brand, shouldSave = true) {
      this.brand = brand === 'MINI' ? 'MINI' : 'BMW';
      this.brandButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.btBrand === this.brand)));
      const codes = Object.keys(this.models || {}).filter(code => this.brandFor(code) === this.brand);
      const generations = (this.brand === 'MINI' ? ['R', 'F'] : ['E', 'F', 'G']).filter(prefix => codes.some(code => code.startsWith(prefix)));
      this.setOptions(this.generation, this.labels.choose_generation, generations.map(prefix => [prefix, this.labels.series.replace('__prefix__', prefix)]), true);
      this.changeGeneration(false);
      if (shouldSave) this.clearSelection();
    }
    restoreSelection() {
      const saved = this.savedSelection();
      const urlModel = this.selectionFromUrl();
      const model = urlModel || String(saved.model || '').toUpperCase();
      const valid = Object.hasOwn(this.models, model);
      this.changeBrand(valid ? this.brandFor(model) : saved.brand, false);
      if (!valid) return;
      this.generation.value = model.charAt(0);
      this.changeGeneration(false);
      this.model.value = model;
      this.changeModel(false);
      if ((!urlModel || urlModel === saved.model) && [...this.category.options].some(option => option.value === saved.category)) this.category.value = saved.category;
      this.updateDestination(false);
      this.saveSelection();
      this.status.textContent = this.labels.saved.replace('__model__', model);
    }
    changeGeneration(shouldSave = true) {
      const prefix = this.generation.value;
      const codes = Object.keys(this.models || {}).filter(code => prefix && code.startsWith(prefix) && this.brandFor(code) === this.brand).sort();
      this.setOptions(this.model, this.labels.choose_model, codes.map(code => [code, code]), codes.length > 0);
      this.setOptions(this.category, this.labels.choose_model_first, [], false);
      this.button.disabled = true;
      this.form.removeAttribute('action');
      this.status.textContent = '';
      if (shouldSave) this.clearSelection();
    }
    changeModel(shouldSave = true) {
      const code = this.model.value;
      const categories = this.models?.[code] || [];
      const entries = categories.map(item => [this.categoryKey(item), item.shared ? `${item.title} (${item.scope})` : item.title]);
      this.setOptions(this.category, this.labels.all_categories, entries, !!code);
      this.status.textContent = code && categories.length === 0 ? this.labels.no_categories : '';
      this.updateDestination(shouldSave);
      if (!code && shouldSave) this.clearSelection();
    }
    destination() {
      const code = this.model.value;
      if (!code || !Object.hasOwn(this.models || {}, code) || this.brandFor(code) !== this.brand) return null;
      const base = this.dataset.catalogUrl.replace(/\/$/, '');
      const item = this.models[code].find(entry => this.categoryKey(entry) === this.category.value);
      const target = item ? (item.url || `${base}/${encodeURIComponent(item.handle)}${item.vehicleTag ? '/' + encodeURIComponent(item.vehicleTag) : ''}`) : base;
      const url = new URL(target, window.location.origin);
      url.searchParams.set(item ? 'bt_vehicle' : 'vehicle', code);
      return url.toString();
    }
    updateDestination(shouldSave = true) {
      const url = this.destination();
      this.button.disabled = !url;
      if (url) this.form.action = url;
      else this.form.removeAttribute('action');
      if (url && shouldSave) this.saveSelection();
    }
    submit(event) {
      event.preventDefault();
      const url = this.destination();
      if (url) window.location.assign(url);
    }
  });
}
